import React, { createContext, useCallback, useState, useContext, useEffect } from 'react';

import { api, CommonHeaderProperties } from '../services/api';
import { useClient } from './client';

interface Account {
  id: string;
  name: string;
  document: string;
  balance: number;
  bank: string;
  branch: string;
  account: string;
  accountDigit: string;
  status: string;
  type: string;
  pin: boolean;
  email: string;
  isPool: boolean;
  provider?: string;
}

/** Contas do mesmo login (a CDC e a Iugu), devolvidas pelo /accounts/session. */
export interface LinkedAccount {
  id: string;
  provider: 'CDC' | 'IUGU';
  name: string;
  status: string;
}

interface AuthState {
  token: string;
  account: Account;
}

interface SignInCredentials {
  document: string;
  password: string;
}

export interface SignInMultiCredentials {
  document: string;
  email: string;
  password: string;
}

interface AuthContextData {
  account: Account;
  linkedAccounts: LinkedAccount[];
  /** Com mais de uma conta, devolve as opções e espera o chooseAccount. */
  signIn(credentials: SignInCredentials): Promise<LinkedAccount[] | undefined>;
  signInMulti(credentials: SignInMultiCredentials): Promise<LinkedAccount[] | undefined>;
  chooseAccount(accountId: string): Promise<void>;
  switchAccount(accountId: string): Promise<void>;
  signOut(): void;
  refresh: () => void;
  refreshAccount: () => void;
  // updateBalance(balance: number): void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

interface AuthProviderProps {
  children: React.ReactNode;
}


export function AuthProvider({ children }: AuthProviderProps) {
  const { client } = useClient();
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem('@stricv2:linked') || '[]');
    } catch {
      return [];
    }
  });
  // Token do login enquanto o cliente escolhe entre a conta CDC e a Iugu.
  const [pendingToken, setPendingToken] = useState('');
  const [data, setData] = useState<AuthState>(() => {
    const token = sessionStorage.getItem('@stricv2:token');
    const account = sessionStorage.getItem('@stricv2:account') as string;


    if (token && account) {
      const accountData = JSON.parse(account);

      api.defaults.headers = {
        Authorization: `Bearer ${token}`,
        account: accountData.id,
      } as CommonHeaderProperties;

      return { token, account: accountData };
    }

    return {} as AuthState;
  });

  async function checkStep(data: Account, token: string) {
    const type = data.type === 'individuals' ? 'pf' : 'pj';
    if (data.status === 'WAITING_ADDRESS') {
      window.localStorage.setItem('@stricv2:status', 'WAITING_ADDRESS');
      window.localStorage.setItem('@stricv2:id', token);
      localStorage.removeItem('@stricv2:token');
      localStorage.removeItem('@stricv2:account');
      window.location.href = `/create-account/?type=${type}`;
      return;
    }

    if (data.status === 'WAITING_DOCUMENTS') {
      window.localStorage.setItem('@stricv2:status', 'WAITING_DOCUMENTS');
      window.localStorage.setItem('@stricv2:id', token);
      localStorage.removeItem('@stricv2:token');
      localStorage.removeItem('@stricv2:account');
      sessionStorage.removeItem('@stricv2:token');
      sessionStorage.removeItem('@stricv2:account');
      window.location.href = `/create-account/?type=${type}`;
      return;
    }

    if (data.status === 'WAITING_ANALYSIS') {
      window.localStorage.setItem('@stricv2:status', 'WAITING_ANALYSIS');
      window.localStorage.setItem('@stricv2:id', token);
      localStorage.removeItem('@stricv2:token');
      localStorage.removeItem('@stricv2:account');
      sessionStorage.removeItem('@stricv2:token');
      sessionStorage.removeItem('@stricv2:account');
      window.location.href = `/create-account/?type=${type}`;
      return;
    }

    if (data.status === 'UNDER_ANALYSIS') {
      window.localStorage.setItem('@stricv2:status', 'UNDER_ANALYSIS');
      localStorage.removeItem('@stricv2:token');
      localStorage.removeItem('@stricv2:account');
      localStorage.removeItem('@stricv2:token');
      localStorage.removeItem('@stricv2:account');
      sessionStorage.removeItem('@stricv2:token');
      sessionStorage.removeItem('@stricv2:account');
      window.location.href = `/create-account/?type=${type}`;
      return;
    }

    return true;
  }

  async function getAccount() {
    const token = window.localStorage.getItem('@stricv2:token') ||
    window.sessionStorage.getItem('@stricv2:token');
    if (token) {
      try {
        api.defaults.headers = {
          Authorization: `Bearer ${token}`,
        } as CommonHeaderProperties;

        const { data } = await api.get('/accounts');

        await checkStep(data, token);

        setData({ token, account: data });
      } catch(err) {
        signOut();
      }
    }
  }

  function refresh() {
    getAccount();

    setTimeout(() => {
      getAccount();
    }, 2000);

    setTimeout(() => {
      getAccount();
    }, 10000);

    setTimeout(() => {
      getAccount();
    }, 60000);
  }

  function refreshAccount() {
    setInterval(() => {
      getAccount();
    }, 60000);
  }


  useEffect(() => {
    getAccount();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Entra na conta do token: o mesmo fluxo de sempre depois do login.
  async function enterAccount(token: string) {
    api.defaults.headers = {
      Authorization: `Bearer ${token}`,
    } as CommonHeaderProperties;

    const { data } = await api.get("/accounts");

    sessionStorage.setItem('@stricv2:token', token);
    sessionStorage.setItem('@stricv2:account', JSON.stringify(data));

    api.defaults.headers = {
      Authorization: `Bearer ${token}`,
      account: data.id,
    } as CommonHeaderProperties;

    await checkStep(data, token);

    setData({ token, account: data });
  }

  // Quem tem conta CDC e Iugu escolhe qual acessar; o token do login é o da CDC.
  async function startSession(token: string, accounts?: LinkedAccount[]) {
    const linked = accounts && accounts.length > 1 ? accounts : [];
    setLinkedAccounts(linked);
    sessionStorage.setItem('@stricv2:linked', JSON.stringify(linked));

    if (linked.length > 1) {
      setPendingToken(token);
      return linked;
    }

    await enterAccount(token);
    return undefined;
  }

  const signIn = useCallback(async ({ document, password }) => {
    window.localStorage.removeItem('@stricv2:status');
    window.localStorage.removeItem('@stricv2:id');

    const response = await api.post('/accounts/session', {
      document,
      password,
    }, {
      headers: {
        client: client.client_id, // "d2afc12c-bc27-440c-96c2-88e006c92ab2"
      }
    });

    const { access_token: token, accounts } = response.data;

    return startSession(token, accounts);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signInMulti = useCallback(async ({ document, email, password }) => {
    window.localStorage.removeItem('@stricv2:status');
    window.localStorage.removeItem('@stricv2:id');

    const response = await api.post('/accounts/session', {
      document,
      email: email.trim().toLowerCase(),
      password,
    }, {
      headers: {
        client: client.client_id,
      }
    });

    const { access_token: token, accounts } = response.data;

    return startSession(token, accounts);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Escolha feita na tela de login, com o token do login (conta CDC).
  const chooseAccount = useCallback(async (accountId: string) => {
    const { data: switched } = await api.post(
      '/accounts/session/switch',
      { accountId },
      { headers: { Authorization: `Bearer ${pendingToken}` } },
    );
    setPendingToken('');
    await enterAccount(switched.access_token);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingToken]);

  // Troca de conta já logado (cabeçalho): recarrega na conta nova.
  const switchAccount = useCallback(async (accountId: string) => {
    const { data: switched } = await api.post('/accounts/session/switch', { accountId });
    await enterAccount(switched.access_token);
    window.location.href = '/';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('@stricv2:token');
    localStorage.removeItem('@stricv2:account');
    sessionStorage.removeItem('@stricv2:token');
    sessionStorage.removeItem('@stricv2:account');
    sessionStorage.removeItem('@stricv2:linked');

    setLinkedAccounts([]);
    setPendingToken('');
    setData({} as AuthState);
  }, []);


  return (
    <AuthContext.Provider
      value={{
        account: data.account,
        linkedAccounts,
        signIn,
        signInMulti,
        chooseAccount,
        switchAccount,
        signOut,
        refresh,
        refreshAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}



